import { CalendarIcon, ClockIcon, TagIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface OrderSummaryProps {
  /**
   * Cohort name
   */
  cohortName: string

  /**
   * Start date
   */
  startDate: string

  /**
   * Duration (e.g., "8 weeks", "12 weeks")
   */
  duration: string

  /**
   * Subtotal amount
   */
  subtotal: number

  /**
   * Discount amount (optional)
   */
  discount?: number

  /**
   * Discount code (optional)
   */
  discountCode?: string

  /**
   * Currency symbol
   * @default "$"
   */
  currency?: string

  /**
   * Additional CSS classes
   */
  className?: string
}

export function OrderSummary({
  cohortName,
  startDate,
  duration,
  subtotal,
  discount = 0,
  discountCode,
  currency = "$",
  className = "",
}: OrderSummaryProps) {
  const total = subtotal - discount

  return (
    <div className={cn("bg-card border border-border rounded-lg overflow-hidden", className)}>
      {/* Header */}
      <div className="bg-muted/50 px-6 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Order Summary</h2>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Cohort Details */}
        <div>
          <h3 className="font-semibold text-foreground mb-3">{cohortName}</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarIcon className="h-4 w-4" />
              <span>Starts {startDate}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ClockIcon className="h-4 w-4" />
              <span>{duration}</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Price Breakdown */}
        <div className="space-y-3">
          {/* Subtotal */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium text-foreground">
              {currency}
              {subtotal.toFixed(2)}
            </span>
          </div>

          {/* Discount */}
          {discount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <TagIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-500" />
                <span className="text-muted-foreground">
                  Discount
                  {discountCode && (
                    <span className="ml-1 text-xs font-medium text-green-600 dark:text-green-500">
                      ({discountCode})
                    </span>
                  )}
                </span>
              </div>
              <span className="font-medium text-green-600 dark:text-green-500">
                -{currency}
                {discount.toFixed(2)}
              </span>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Total */}
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-foreground">Total</span>
            <span className="text-2xl font-bold text-foreground">
              {currency}
              {total.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Info Note */}
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            You'll get immediate access to all course materials after payment. Your
            enrollment will be confirmed via email.
          </p>
        </div>
      </div>
    </div>
  )
}